import React, { useEffect, useMemo, useRef } from 'react';
import WizardOverlay from './WizardOverlay';
import WizardSummarySidebar, { WizardSummaryDisclosure } from './WizardSummarySidebar';
import PricingStep from './steps/PricingStep';
import FinancingStep from './steps/FinancingStep';
import TermsStep from './steps/TermsStep';
import ReviewStep from './steps/ReviewStep';
import { useOfferForm } from '../hooks/useOfferForm';
import { FORM_STEPS, formatPrice } from '../utils';
import {
  CURE_ELECTION_FIELD,
  FINANCING_TERMS_FIELD,
  FINANCING_TERM_KEYS,
  TITLE_PERIODS_FIELD,
  TITLE_PERIOD_KEYS,
  cureElectionAsked,
  financingTermsAsked,
  getSelectedContingencyKeys,
  parseContingencyElectionError,
  serializeContingencies,
} from '../contingencyCatalog';
import { ContingencyErrorBanner } from '../ContingencyElectionErrors';
import './OfferWizard.css';

/**
 * OfferWizard — orchestrator that owns wizard form state and renders the
 * full-screen overlay + active step + sidebar + sticky footer.
 */
export default function OfferWizard({
  open,
  onClose,
  property,
  buyerId,
  onSubmit,
  submitting = false,
  submitError = null,
  // The API error's `detail`; a 422 `offer_contingencies_invalid` is shown
  // next to the contingencies it names.
  submitErrorDetail = null,
}) {
  const wiz = useOfferForm({ buyerId, property });
  const initialFocusRef = useRef(null);

  // A contingency refusal applies to the elections that were submitted; once
  // the buyer changes them it no longer describes the form.
  const contingencyError = useMemo(() => parseContingencyElectionError(submitErrorDetail), [submitErrorDetail]);
  const submittedContingencies = useRef(null);
  // Synchronous double-fire guard for the final Submit: `submitting` is a prop
  // the parent flips asynchronously, so two clicks in the same tick can both
  // pass the guard before the button disables — a duplicate offer. The ref locks
  // immediately on the first valid submit and releases once the parent's submit
  // settles — including a parent that bails out early without ever flipping
  // `submitting` (e.g. no seller selected), which would otherwise leave the
  // lock stuck and Submit dead.
  const submitLockRef = useRef(false);
  const { setStep } = wiz;
  useEffect(() => {
    if (!contingencyError) return;
    // Take the buyer to the contingencies the server named.
    setStep(2);
  }, [contingencyError, setStep]);
  const contingencyErrorActive =
    Boolean(contingencyError) && wiz.form.contingencies === submittedContingencies.current;

  // Compute "dirty": user has changed something meaningful from defaults.
  const dirty = useMemo(() => {
    const listPrice = property?.price || property?.list_price || 0;
    // Contingencies now start empty, so any selection at all is a change.
    const c = wiz.form.contingencies || {};
    const contingenciesChanged =
      getSelectedContingencyKeys(c).length > 0 || !!c.other || !!c.lead_paint_election
      || !!c.seller_cure_right
      || [...FINANCING_TERM_KEYS, ...TITLE_PERIOD_KEYS].some((key) => c[key] !== undefined && c[key] !== '');
    const earnest = wiz.form.earnest_money ? Number(wiz.form.earnest_money) : 0;
    return (
      Number(wiz.form.offer_price) !== listPrice ||
      contingenciesChanged ||
      earnest > 0 ||
      !!(wiz.form.additional_terms && wiz.form.additional_terms.trim())
    );
  }, [wiz.form, property]);

  // Reset form shortly after the overlay closes.
  useEffect(() => {
    if (open) return undefined;
    const t = setTimeout(() => {
      wiz.reset();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stepNode = (() => {
    switch (wiz.step) {
      case 0: return <PricingStep wiz={wiz} property={property} />;
      case 1: return <FinancingStep wiz={wiz} property={property} />;
      case 2: return (
        <TermsStep
          wiz={wiz}
          property={property}
          serverErrors={contingencyErrorActive ? contingencyError.fieldErrors : null}
        />
      );
      case 3: return <ReviewStep wiz={wiz} property={property} onJumpToStep={(i) => wiz.setStep(i)} />;
      default: return null;
    }
  })();

  const onContinue = () => {
    if (wiz.step < 3 && wiz.canAdvance[wiz.step]) {
      wiz.setStep(wiz.step + 1);
    }
  };

  const onSubmitClick = () => {
    if (submitLockRef.current || submitting || !wiz.canAdvance.every(Boolean)) return;
    if (typeof onSubmit === 'function') {
      submitLockRef.current = true;
      submittedContingencies.current = wiz.form.contingencies;
      // Deadlines go out as whole numbers, and only for contingencies that
      // are on. Every offered contingency is sent as an explicit election,
      // so "none selected" is all-false rather than omitted: the server
      // refuses an offer whose contingencies are omitted (land, residential
      // and condo). The lead-paint and cure elections go out only where
      // they are asked, and the title periods and loan terms only on a WI
      // offer.
      const explicitKeys = wiz.contingencyOptions.map((o) => o.key);
      const release = () => { submitLockRef.current = false; };
      Promise.resolve(onSubmit({
        ...wiz.form,
        contingencies: serializeContingencies(wiz.form.contingencies, {
          explicitKeys,
          leadPaintMode: wiz.leadPaintMode,
          cureCategory: wiz.cureCategory,
          termsCategory: wiz.termsCategory,
        }),
      })).then(release, release);
    }
  };

  const continueDisabled = !wiz.canAdvance[wiz.step];
  // Belt and braces: the Review step is only reached with valid terms, but
  // never submit elections the Terms step would refuse.
  const submitBlocked = !wiz.canAdvance.every(Boolean);
  const continueTitle = continueDisabled ? wiz.errors[wiz.step] || '' : '';
  const isReview = wiz.step === 3;

  const footer = (
    <div className="offer-wizard__footer">
      {contingencyError ? (
        contingencyErrorActive && (
          <ContingencyErrorBanner
            error={contingencyError}
            options={wiz.contingencyOptions}
            extraKeys={[
              ...(wiz.leadPaintMode ? ['lead_paint'] : []),
              ...(cureElectionAsked(wiz.cureCategory, wiz.form.contingencies) ? [CURE_ELECTION_FIELD] : []),
              ...(financingTermsAsked(wiz.termsCategory, wiz.form.contingencies) ? [FINANCING_TERMS_FIELD] : []),
              ...(wiz.termsCategory ? [TITLE_PERIODS_FIELD] : []),
            ]}
          />
        )
      ) : (
        submitError && <p role="alert" className="offer-wizard__error">{submitError}</p>
      )}
      <div className="offer-wizard__footer-actions">
        <button
          type="button"
          className="offer-wizard__btn offer-wizard__btn--secondary"
          onClick={() => wiz.setStep(Math.max(0, wiz.step - 1))}
          disabled={wiz.step === 0}
        >
          ← Back
        </button>
        {!isReview ? (
          <button
            ref={initialFocusRef}
            type="button"
            className="offer-wizard__btn offer-wizard__btn--primary"
            onClick={onContinue}
            disabled={continueDisabled}
            title={continueTitle}
            aria-describedby={continueDisabled && continueTitle ? 'offer-wizard-continue-help' : undefined}
          >
            Continue →
          </button>
        ) : (
          <button
            ref={initialFocusRef}
            type="button"
            className="offer-wizard__btn offer-wizard__btn--primary offer-wizard__btn--submit"
            onClick={onSubmitClick}
            disabled={submitting || submitBlocked}
            title={submitBlocked ? wiz.errors[2] || wiz.errors[0] || wiz.errors[1] || '' : undefined}
          >
            {submitting ? 'Submitting…' : `Submit Offer · ${formatPrice(wiz.derived.offerPrice)}`}
          </button>
        )}
      </div>
      {continueDisabled && continueTitle && !isReview && (
        <p id="offer-wizard-continue-help" className="offer-wizard__help">{continueTitle}</p>
      )}
    </div>
  );

  const address = property?.address || property?.property_address || '';
  const title = `Make an offer${address ? ` · ${address}` : ''}`;

  return (
    <WizardOverlay
      open={open}
      onClose={onClose}
      title={title}
      step={wiz.step}
      steps={FORM_STEPS}
      footer={footer}
      summary={<WizardSummarySidebar wiz={wiz} property={property} />}
      dirty={dirty}
      initialFocusRef={initialFocusRef}
    >
      {stepNode}
      {/* Mobile/tablet summary disclosure rendered at the bottom of the active
          step content — this keeps the footer free of competing surfaces and
          lets the summary scroll with the rest of the step body. */}
      <WizardSummaryDisclosure wiz={wiz} property={property} />
    </WizardOverlay>
  );
}
